import React, { useState } from 'react';
import { MessageSquare, Mail, Check, StickyNote, X, ChevronDown, ChevronUp, Trash2, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'message', label: 'Messages' },
  { id: 'submission', label: 'Submissions' },
  { id: 'joinpack', label: 'Join the Pack' },
];

const STATUS_COLORS = {
  'New': 'bg-swp-ice/15 text-swp-ice border-swp-ice/25',
  'Read': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  'Replied': 'bg-green-500/20 text-green-400 border-green-500/40',
  'Reviewed': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  'Archived': 'bg-gray-500/20 text-swp-white-ghost border-gray-500/40',
};

const TYPE_BADGE = {
  'message': 'bg-purple-500/20 text-purple-400 border-purple-500/40',
  'submission': 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  'joinpack': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
};

const DashboardActivity = ({ activityItems, formatDate, formatTimeAgo, onRefresh }) => {
  const [activityFilter, setActivityFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [noteModal, setNoteModal] = useState({ open: false, item: null, text: '' });
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  const filteredActivity = activityFilter === 'all' ? activityItems : activityItems.filter(i => i._type === activityFilter);
  const newCount = activityItems.filter(i => i.status === 'New').length;
  const counts = {
    all: activityItems.length,
    message: activityItems.filter(i => i._type === 'message').length,
    submission: activityItems.filter(i => i._type === 'submission').length,
    joinpack: activityItems.filter(i => i._type === 'joinpack').length,
  };

  const handleStatusChange = async (item, newStatus, e) => {
    if (e) e.stopPropagation();
    const endpoint = item._type === 'message' ? 'contact' : 'submissions';
    try {
      const response = await fetch(`${API}/api/${endpoint}/${item.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) { toast.success(`Status updated to ${newStatus}`); onRefresh(); }
    } catch { toast.error('Failed to update status'); }
  };

  const handleSaveNote = async () => {
    if (!noteModal.item) return;
    const endpoint = noteModal.item._type === 'message' ? 'contact' : 'submissions';
    try {
      const response = await fetch(`${API}/api/${endpoint}/${noteModal.item.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: noteModal.text })
      });
      if (response.ok) { toast.success('Note saved'); setNoteModal({ open: false, item: null, text: '' }); onRefresh(); }
    } catch { toast.error('Failed to save note'); }
  };

  const handleDelete = async (item, permanent = false) => {
    if (!window.confirm(permanent ? `Permanently delete from "${item.name}"?` : `Archive from "${item.name}"?`)) return;
    const endpoint = item._type === 'message' ? 'contact' : 'submissions';
    try {
      const url = permanent ? `${API}/api/${endpoint}/${item.id}?permanent=true` : `${API}/api/${endpoint}/${item.id}`;
      const response = await fetch(url, { method: 'DELETE' });
      if (response.ok) { toast.success(permanent ? 'Deleted' : 'Archived'); onRefresh(); }
    } catch { toast.error('Failed to delete'); }
  };

  const getStatusOptions = (type) => {
    if (type === 'message') return ['New', 'Read', 'Replied', 'Archived'];
    if (type === 'submission') return ['New', 'Reviewed', 'Archived'];
    return [];
  };

  const handleSelectItem = (itemKey, e) => {
    e.stopPropagation();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.has(itemKey) ? newSet.delete(itemKey) : newSet.add(itemKey);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedItems(selectedItems.size === filteredActivity.length ? new Set() : new Set(filteredActivity.map(i => `${i._type}-${i.id}`)));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedItems.size === 0) return;
    const selectedList = filteredActivity.filter(i => selectedItems.has(`${i._type}-${i.id}`));
    if (bulkAction === 'delete' && !window.confirm(`Delete ${selectedList.length} items? This cannot be undone.`)) return;

    let successCount = 0;
    for (const item of selectedList) {
      if (item._type === 'joinpack' && bulkAction !== 'delete') continue;
      const endpoint = item._type === 'message' ? 'contact' : item._type === 'submission' ? 'submissions' : 'contact/cineconnect-interest';
      try {
        let response;
        if (bulkAction === 'delete') {
          response = await fetch(`${API}/api/${endpoint}/${item.id}?permanent=true`, { method: 'DELETE' });
        } else if (bulkAction === 'read') {
          response = await fetch(`${API}/api/${endpoint}/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'Read' }) });
        } else if (bulkAction === 'archive') {
          response = await fetch(`${API}/api/${endpoint}/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'Archived' }) });
        }
        if (response?.ok) successCount++;
      } catch { /* continue */ }
    }
    toast.success(`${bulkAction === 'delete' ? 'Deleted' : 'Updated'} ${successCount} items`);
    setBulkAction('');
    onRefresh();
  };

  return (
    <div>
      {/* Filter Chips */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="activity-filters">
        {TYPE_FILTERS.map(f => (
          <button key={f.id} onClick={() => setActivityFilter(f.id)}
            className={`px-4 py-2 rounded-sm text-sm font-mono uppercase tracking-widest transition-all flex items-center gap-2 ${
              activityFilter === f.id ? 'bg-white text-black' : 'bg-swp-surface text-swp-white-ghost border border-swp-border hover:text-swp-white hover:border-gray-500'
            }`} data-testid={`filter-${f.id}`}>
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded ${activityFilter === f.id ? 'bg-swp-black/10' : 'bg-white/10'}`}>{counts[f.id]}</span>
          </button>
        ))}
        {newCount > 0 && <span className="px-3 py-2 bg-swp-ice text-white text-sm rounded-sm">{newCount} new</span>}
      </div>

      {/* Bulk Actions */}
      {filteredActivity.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-swp-surface border border-swp-border rounded-swp">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={selectedItems.size === filteredActivity.length && filteredActivity.length > 0} onChange={handleSelectAll} className="w-4 h-4 rounded border-gray-600 bg-swp-black text-swp-ice focus:ring-swp-ice" />
            <span className="text-swp-white-ghost text-sm">{selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select all'}</span>
          </label>
          {selectedItems.size > 0 && (
            <>
              <div className="h-4 w-px bg-gray-700" />
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="bg-swp-black border border-swp-border rounded px-3 py-1.5 text-sm text-swp-white-dim focus:border-swp-ice focus:outline-none">
                <option value="">Bulk Actions...</option>
                <option value="read">Mark as Read</option>
                <option value="archive">Archive All</option>
                <option value="delete">Delete All</option>
              </select>
              <button onClick={handleBulkAction} disabled={!bulkAction} className="px-3 py-1.5 bg-swp-ice hover:bg-swp-ice disabled:bg-swp-muted disabled:cursor-not-allowed text-white text-sm rounded transition-colors">Apply</button>
            </>
          )}
        </div>
      )}

      {/* Activity Items */}
      {filteredActivity.length === 0 ? (
        <div className="text-center py-12 bg-swp-surface border border-swp-border rounded-swp">
          <MessageSquare className="w-12 h-12 text-gray-700 mx-auto mb-4" />
          <p className="text-swp-white-ghost">No activity found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredActivity.map((item) => {
            const itemKey = `${item._type}-${item.id}`;
            const isSelected = selectedItems.has(itemKey);
            return (
              <div key={itemKey} className={`bg-swp-surface border rounded-swp overflow-hidden transition-colors ${isSelected ? 'border-swp-ice' : item.status === 'New' ? 'border-swp-ice/30' : 'border-swp-border hover:border-swp-border'}`} data-testid={`activity-item-${item.id}`}>
                <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(expandedId === itemKey ? null : itemKey)}>
                  <input type="checkbox" checked={isSelected} onChange={(e) => handleSelectItem(itemKey, e)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-gray-600 bg-swp-black text-swp-ice focus:ring-swp-ice flex-shrink-0" />
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase border ${TYPE_BADGE[item._type]}`}>{item._type === 'joinpack' ? 'Join the Pack' : item._type}</span>
                    <span className={`px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase border ${STATUS_COLORS[item.status] || STATUS_COLORS['New']}`}>{item.status}</span>
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold truncate">{item.name}</h3>
                      <p className="text-swp-white-ghost/70 text-sm truncate">{item.email}</p>
                    </div>
                    {item.admin_notes && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] rounded-sm" title={item.admin_notes}>Note</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {item._type !== 'joinpack' && item.status === 'New' && <button onClick={(e) => handleStatusChange(item, 'Read', e)} className="p-1.5 text-swp-white-ghost/70 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors" title="Mark as Read"><Check size={16} /></button>}
                    {item._type !== 'joinpack' && <button onClick={(e) => { e.stopPropagation(); setNoteModal({ open: true, item, text: item.admin_notes || '' }); }} className={`p-1.5 hover:bg-amber-500/10 rounded transition-colors ${item.admin_notes ? 'text-amber-400' : 'text-swp-white-ghost/70 hover:text-amber-400'}`} title="Add/Edit Note"><StickyNote size={16} /></button>}
                    <a href={`mailto:${item.email}`} onClick={(e) => e.stopPropagation()} className="p-1.5 text-swp-white-ghost/70 hover:text-swp-ice hover:bg-swp-ice/10 rounded transition-colors" title="Reply via Email"><Mail size={16} /></a>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(item, true); }} className="p-1.5 text-swp-white-ghost/70 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Delete"><Trash2 size={16} /></button>
                    <span className="hidden lg:block text-swp-white-ghost/70 text-sm ml-2">{formatDate(item.created_at)}</span>
                    {expandedId === itemKey ? <ChevronUp size={20} className="text-swp-white-ghost ml-2" /> : <ChevronDown size={20} className="text-swp-white-ghost ml-2" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === itemKey && (
                  <div className="px-6 py-6 border-t border-swp-border bg-swp-deep/50">
                    {item._type === 'message' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Email</label><p className="text-white mt-1"><a href={`mailto:${item.email}`} className="text-swp-ice hover:underline">{item.email}</a></p></div>
                          {item.phone && <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Phone</label><p className="text-white mt-1">{item.phone}</p></div>}
                          {item.service && <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Project Type</label><p className="text-white mt-1 capitalize">{item.service.replace('-', ' ')}</p></div>}
                        </div>
                        <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Message</label><p className="text-swp-white-dim mt-2 whitespace-pre-wrap leading-relaxed">{item.message}</p></div>
                      </div>
                    )}
                    {item._type === 'submission' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Role</label><p className="text-white mt-1">{item.role}</p></div>
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Type</label><p className="text-white mt-1">{item.submission_type}</p></div>
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Stage</label><p className="text-white mt-1">{item.project_stage}</p></div>
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Genres</label><div className="flex flex-wrap gap-2 mt-1">{item.genres?.map((g) => <span key={g} className="px-2 py-1 bg-gray-800 text-swp-white-dim rounded text-sm">{g}</span>)}</div></div>
                        </div>
                        <div className="space-y-4">
                          <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Logline</label><p className="text-white mt-1 leading-relaxed">{item.logline}</p></div>
                          {item.external_link && <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Link</label><a href={item.external_link} target="_blank" rel="noopener noreferrer" className="text-swp-ice hover:underline mt-1 flex items-center gap-2">View <ExternalLink size={14} /></a></div>}
                          {item.message && <div><label className="text-swp-white-ghost/70 text-xs font-mono uppercase tracking-widest">Message</label><p className="text-swp-white-dim mt-1">{item.message}</p></div>}
                        </div>
                      </div>
                    )}
                    {item._type === 'joinpack' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2"><Users size={16} className="text-cyan-400" /><span className="text-swp-white-dim">{item.name} wants to be notified when Join the Pack launches.</span></div>
                        <p className="text-swp-white-ghost/70 text-sm">Email: <a href={`mailto:${item.email}`} className="text-swp-ice hover:underline">{item.email}</a></p>
                      </div>
                    )}
                    {item._type !== 'joinpack' && (
                      <div className="flex items-center justify-between mt-6 pt-6 border-t border-swp-border">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-swp-white-ghost/70 text-sm mr-2">Set Status:</span>
                          {getStatusOptions(item._type).filter(s => s !== item.status).map(status => (
                            <button key={status} onClick={() => handleStatusChange(item, status)} className={`px-3 py-1 rounded-sm text-xs font-mono uppercase border transition-colors hover:opacity-80 ${STATUS_COLORS[status]}`}>{status}</button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={`mailto:${item.email}`} className="p-2 text-swp-white-ghost hover:text-swp-ice transition-colors" title="Reply"><Mail size={18} /></a>
                          <button onClick={() => handleDelete(item, true)} className="p-2 text-swp-white-ghost hover:text-red-400 transition-colors" title="Delete"><Trash2 size={18} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Note Modal */}
      {noteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-swp-deep/90">
          <div className="bg-swp-surface border border-swp-border rounded-swp w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-swp-border">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><StickyNote size={18} className="text-amber-400" /> Admin Note</h3>
              <button onClick={() => setNoteModal({ open: false, item: null, text: '' })} className="p-1 text-swp-white-ghost hover:text-swp-white"><X size={20} /></button>
            </div>
            <div className="p-4">
              <p className="text-swp-white-ghost text-sm mb-2">For: {noteModal.item?.name}</p>
              <textarea value={noteModal.text} onChange={(e) => setNoteModal(prev => ({ ...prev, text: e.target.value }))} rows={4} className="w-full bg-swp-black border border-swp-border rounded-swp px-4 py-3 text-white focus:border-swp-ice focus:outline-none resize-none" placeholder="Add internal notes..." data-testid="note-textarea" />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-swp-border">
              <button onClick={() => setNoteModal({ open: false, item: null, text: '' })} className="px-4 py-2 border border-swp-border text-swp-white-ghost rounded-swp hover:text-swp-white text-sm">Cancel</button>
              <button onClick={handleSaveNote} className="px-4 py-2 bg-swp-ice text-white rounded-swp text-sm" data-testid="save-note-btn">Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardActivity;
